import { onMounted } from "vue";
import type { IComposableInterface } from "./types";
import { useCommon } from "./use-common";
import { ValidationError } from "./validation.error";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useDirectBackend } from "#imports";
import { useLocale } from "~/composables/use-locale";
import { isUsableCoordinate } from "~/utils/is-usable-coordinate";

export const useNewAddress = (): IComposableInterface => {
  const business = useBusinessStore();
  const basket = useBasketStore();
  const customer = useCustomerStore();

  const { customerRepo } = useDirectBackend();
  const { t } = useLocale();

  const {
    formErrors,
    isLoading,
    isSaving,
    record,
    setFieldValue,
    getNicknameLabel,
    noop,
    handleSaveError,
  } = useCommon();

  const saveAddress = async (onSuccess: () => void = noop) => {
    formErrors.value = {};
    isSaving.value = true;

    try {
      if (!isUsableCoordinate(record.value)) {
        throw new ValidationError("Address missing coordinates", {
          lat: t("components.address_form.error_message_coordinates"),
        });
      }

      const data = await customerRepo.createAddress({
        nickname: getNicknameLabel(record.value.nickname),
        coordinate: {
          lat: record.value.lat,
          lng: record.value.lng,
        },
        city: record.value.city ?? "",
        number: record.value.number,
        location: record.value.location ?? "",
        zipCode: record.value.zipCode,
        instruction: record.value.instruction,
        country: business.country,
      });

      await Promise.all([
        basket.setAddress({
          lat: record.value.lat,
          lng: record.value.lng,
          addressId: data.addressId,
          address: record.value?.location,
          city: record.value?.city,
          number: record.value?.number,
          country: business.country,
          postalCode: record.value?.zipCode,
          instructions: record.value?.instruction,
        }),
        customer.syncAddresses(basket.businessId),
      ]);

      onSuccess();
    } catch (e) {
      handleSaveError(e);
    } finally {
      isSaving.value = false;
    }
  };

  const loadAddress = () => {
    if (!basket.currentLocation) {
      return;
    }

    const address = basket.currentLocation;

    record.value = {
      location: address.address,
      number: "",
      instruction: "",
      zipCode: address.postalCode ?? "",
      city: address.city ?? "",
      lat: address.coordinate.lat,
      lng: address.coordinate.lng,
      nickname: "home",
    };
  };

  onMounted(() => {
    loadAddress();
    isLoading.value = false;
  });

  return {
    isLoading,
    isSaving,
    record,
    formErrors,
    setFieldValue,
    saveAddress,
  };
};
